import React, { isValidElement, useEffect, useCallback } from 'react'
import '../../assets/css/components/Display/Modal.css'
import { AnimatePresence, AnimateSharedLayout, motion } from 'framer-motion'
// import Button from '../General/Button';
import { IoClose } from 'react-icons/io5'

/**
 * Componente modal, utilizado para mostrar otros componentes
 * de react dentro de un recuadro central que opaca el resto de
 * la ventana (modal)
 * @param {ReactElement} children de react que se desea mostrar
 * @param {boolean} display que controla si el modal se muestra o no
 * @param {funcion} onClose que se activa al dar click en la 'x' del modal
 * @param {Function} onAccept  funcion que se llama al dar click en el
 * boton por defecto de aceptar en el modal
 * @param {string} onAcceptText texto que se muestra en el boton que
 * tiene el modal por defecto para aceptar
 * @param {visible|scroll} scroll  controla el funcionalidad de scroll en el
 * modal; particularmente util para componentes mas grandes que el modal mismo
 * @param {string} onCloseText texto que se muestra en el boton que
 * tiene el modal por defecto para cancelar o cerrar
 * @param {string} modalClasses classes de tailwind para customizar principalmente el
 * tamaño del modal
 * @param {boolean} defaultModal para forzar el modal a mostrar su estilo por defecto
 */
const Modal: React.FC<ModalProps> = ({
  children,
  display,
  onClose,
  onAccept,
  onAcceptText,
  scroll = 'visible',
  onCloseText,
  normalClasses,
  xlClasses,
  lgClasses,
  innerStyle = {},
  defaultModal = true,
  modalRef,
  isFullHeight = false,
  hideClose = false,
  externalHeight = null,
  contentStyle = {},
  backGroundStyle = {},
  ...props
}) => {
  // const defaultLClasses = 'absolute bg-white default-modal rounded-xl';
  const callback = useCallback(
    (e) => {
      if (e.code === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (display) {
      window.addEventListener('keydown', callback)
    }
    return () => {
      window.removeEventListener('keydown', callback)
    }
  }, [display, callback])

  return (
    <AnimateSharedLayout type="crossfade">
      <AnimatePresence>
        {display && (
          <>
            <motion.div
              id="background"
              style={{
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 100,
                height: externalHeight ? externalHeight : '',
                ...backGroundStyle
              }}
              className="absolute w-full max-w-full h-screen top-0 left-0 flex items-center justify-center overflow-hidden"
              initial={{ background: 'rgba(0, 0, 0, 0)' }}
              animate={{ background: 'rgba(0, 0, 0, 0.25)' }}
              exit={{ background: 'rgba(0, 0, 0, 0)' }}
            >
              <motion.div
              ref={modalRef}
                id="content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.05 }}
                className={`${normalClasses || 'w-96'} ${
                  xlClasses || 'xl:w-2/4'
                } ${
                  lgClasses || 'lg:px-4 lg:py-2'
                }  bg-white dark:bg-dark-lightModal shadow-2xl rounded flex flex-col relative overflow-auto ${
                  defaultModal && 'default-modal'
                }`}
                style={{ zIndex: 101, ...contentStyle }}
              >
                { !hideClose && (
                <div className="absolute right-4 top-2.5">
                  <div className="close-wrapper" onClick={() => onClose()}>
                    <IoClose className="m-0.5" />
                  </div>
                </div>
                ) }
                <div
                  className={`w-full py-2 ${
                    scroll === 'scroll' && 'overflow-scroll'
                  } ${scroll === 'visible' && 'overflow-visible'}
                  } flex flex-col ${isFullHeight && 'h-full'}`}
                >
                  <div className='dark:bg-dark-lightModal p-8' style={innerStyle}>
                    {React.Children.map(children, (child: any) => {
                      if (isValidElement(child))
                        return React.cloneElement(
                          child as React.ReactElement<any>,
                          { onClose }
                        )
                      return child
                    })}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </AnimateSharedLayout>
  )
}

export default Modal
interface ModalProps {
  children?: any
  normalClasses?: string
  xlClasses?: string
  lgClasses?: string
  innerStyle?: Object
  scroll?: 'visible' | 'scroll'
  display: boolean
  onClose: Function
  onAccept?: Function
  onCloseText?: string
  onAcceptText?: string
  defaultModal?: boolean 
  modalRef?: any
  isFullHeight?: boolean
  hideClose?: boolean
  externalHeight?: number | null
  contentStyle?: React.CSSProperties
  backGroundStyle?:  React.CSSProperties
}
